import { PropertyWalletHistoryItemDTO } from '../../../services/properties';
import { theme } from '../../../styles';

export const processEventIconStyle = {
  color: theme.main.color,
  margin: 0
} as const;

const EVENT_TYPE_LABELS: Record<string, string> = {
  CLAIM: 'Sinistro',
  CAPITALIZATION: 'Capitalização',
  LENDER_INSURANCE: 'Seguro prestamista',
  FIRE_INSURANCE: 'Seguro incêndio',
  BOND_INSURANCE: 'Fiança',
  ORDER: 'Vistoria',
  PROPERTY_CREATED: 'Imóvel cadastrado',
  PROPERTY_UPDATED: 'Imóvel atualizado',
  PROPERTY: 'Imóvel'
};

const CLAIM_TYPE_LABELS: Record<string, string> = {
  property_damage: 'Dano ao imóvel',
  fire_damage: 'Incêndio',
  rent_claim: 'Aluguel',
  general_claim: 'Sinistro geral'
};

const STATUS_LABELS: Record<string, string> = {
  open: 'Aberto',
  closed: 'Encerrado',
  active: 'Ativo',
  hired: 'Contratado',
  cancelled: 'Cancelado',
  expired: 'Expirado',
  renewed: 'Renovado',
  in_progress: 'Em andamento'
};

export const getHistoricEventDate = (
  item: PropertyWalletHistoryItemDTO
): string | undefined => item.occurredAt || item.date;

export const formatHistoricEventTitle = (item: PropertyWalletHistoryItemDTO): string => {
  if (item.eventType && EVENT_TYPE_LABELS[item.eventType]) {
    return EVENT_TYPE_LABELS[item.eventType];
  }

  if (item.inspectionType) return item.inspectionType;
  if (item.code) return `Evento ${item.code}`;
  return 'Evento registrado';
};

const formatSummaryValue = (value: unknown): string | undefined => {
  if (value == null || value === '') return undefined;
  return String(value);
};

export const formatHistoricEventMeta = (
  item: PropertyWalletHistoryItemDTO,
  isCondominium = false
): string[] => {
  const lines: string[] = [];
  const summary = item.infosSummary || {};

  if (item.code) {
    lines.push(`Protocolo: ${item.code}`);
  }

  const status = formatSummaryValue(summary.status);
  if (status) {
    lines.push(`Status: ${STATUS_LABELS[status] || status}`);
  }

  if (item.eventType === 'CLAIM') {
    const claimType = formatSummaryValue(summary.claim_type);
    if (claimType) {
      lines.push(`Tipo: ${CLAIM_TYPE_LABELS[claimType] || claimType}`);
    }
  }

  if (item.eventType === 'CAPITALIZATION') {
    const tenantName = formatSummaryValue(summary.tenant_name);
    const lessorName = formatSummaryValue(summary.lessor_name);
    if (tenantName)
      lines.push((isCondominium ? 'Síndico: ' : 'Inquilino: ') + tenantName);
    if (lessorName) lines.push(`Locador: ${lessorName}`);
  }

  if (item.eventType === 'LENDER_INSURANCE') {
    const tenantName = formatSummaryValue(summary.tenant_name);
    const ownerName = formatSummaryValue(summary.owner_name);
    const sourceType = formatSummaryValue(summary.source_type);
    if (tenantName)
      lines.push((isCondominium ? 'Síndico: ' : 'Locatário: ') + tenantName);
    if (ownerName) lines.push(`Proprietário: ${ownerName}`);
    if (sourceType) lines.push(`Origem: ${sourceType}`);
  }

  if (item.identifier) {
    lines.push(`Identificador: ${item.identifier}`);
  }

  const remoteId = item.remoteId || item.orderId;
  if (remoteId && item.eventType === 'CLAIM') {
    lines.push(`Sinistro: ${remoteId}`);
  } else if (remoteId && item.eventType === 'CAPITALIZATION') {
    lines.push(`Capitalização: ${remoteId}`);
  } else if (remoteId && item.eventType === 'LENDER_INSURANCE') {
    lines.push(`Prestamista: ${remoteId}`);
  } else if (remoteId && !item.code) {
    lines.push(`Ref.: ${remoteId}`);
  }

  return lines;
};

export const getHistoricClaimLink = (
  item: PropertyWalletHistoryItemDTO
): string | undefined => {
  if (item.eventType !== 'CLAIM') return undefined;
  const claimId = item.remoteId || item.orderId;
  if (!claimId) return undefined;
  return `claim_estates?claimId=${encodeURIComponent(claimId)}`;
};

export const getHistoricCapitalizationLink = (
  item: PropertyWalletHistoryItemDTO
): string | undefined => {
  if (item.eventType !== 'CAPITALIZATION') return undefined;
  const capitalizationId = item.remoteId || item.orderId;
  if (!capitalizationId) return undefined;
  return `capitalizations?id=${encodeURIComponent(capitalizationId)}`;
};

export const getHistoricLenderInsuranceLink = (
  item: PropertyWalletHistoryItemDTO
): string | undefined => {
  if (item.eventType !== 'LENDER_INSURANCE') return undefined;
  const lenderInsuranceId = item.remoteId || item.orderId;
  if (!lenderInsuranceId) return undefined;
  return `lender-insurances?id=${encodeURIComponent(lenderInsuranceId)}`;
};

export const PROCESS_ICON_CONFIG = [
  {
    key: 'fireInsurance' as const,
    label: 'Seguro incêndio ativo',
    icon: 'fire' as const
  },
  {
    key: 'bondInsurance' as const,
    label: 'Fiança ativa',
    icon: 'file alternate outline' as const
  },
  {
    key: 'claim' as const,
    label: 'Sinistro ativo',
    icon: 'warning sign' as const
  },
  {
    key: 'order' as const,
    label: 'Vistoria ativa',
    icon: 'clipboard check' as const
  },
  {
    key: 'capitalization' as const,
    label: 'Capitalização ativa',
    icon: 'money bill alternate outline' as const
  },
  {
    key: 'lenderInsurance' as const,
    label: 'Prestamista ativo',
    icon: 'shield' as const
  }
];
